import ExcelJS from 'exceljs';
import { parseCsv } from './csv';

export interface ParsedRow {
  [key: string]: string | number | boolean | undefined;
}

export interface ParseResult {
  data: ParsedRow[];
  headers: string[];
}

export const parseFile = async (file: File): Promise<ParseResult> => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase();

  if (fileExtension === 'csv') {
    return parseCSV(file);
  } else if (fileExtension === 'xlsx') {
    return parseExcel(file);
  } else {
    throw new Error('Unsupported file type. Please use CSV or XLSX files.');
  }
};

// Number('+46701234567') is 46701234567, which drops the country code.
// Only values that are already plain numbers should be coerced.
const isPlainNumber = (value: string): boolean =>
  /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value);

const parseCSV = async (file: File): Promise<ParseResult> => {
  const rows = parseCsv(await file.text()).filter((row) =>
    row.some((cell) => cell.trim()),
  );

  if (rows.length === 0) {
    return { data: [], headers: [] };
  }

  // Parse headers
  const headers = rows[0];

  // Parse data rows
  const data: ParsedRow[] = [];
  for (let i = 1; i < rows.length; i++) {
    const values = rows[i];
    const rowData: ParsedRow = {};

    headers.forEach((header, index) => {
      const value = values[index]?.trim();
      if (value === undefined || value === '') {
        rowData[header] = undefined;
      } else if (isPlainNumber(value)) {
        rowData[header] = Number(value);
      } else {
        rowData[header] = value;
      }
    });

    data.push(rowData);
  }

  return { data, headers };
};

const parseExcel = async (file: File): Promise<ParseResult> => {
  const buffer = await file.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);
  const worksheet = workbook.worksheets[0];

  return worksheetToJSON(worksheet);
};

const worksheetToJSON = (worksheet: ExcelJS.Worksheet): ParseResult => {
  if (!worksheet || worksheet.rowCount === 0) {
    return { data: [], headers: [] };
  }

  const headers: string[] = [];
  const data: ParsedRow[] = [];

  // Get headers from first row
  const headerRow = worksheet.getRow(1);
  headerRow.eachCell((cell) => {
    headers.push(cell.value?.toString() || '');
  });

  if (headers.length === 0) {
    return { data: [], headers: [] };
  }

  // Process data rows (starting from row 2)
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return; // Skip header row

    const rowData: ParsedRow = {};
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const header = headers[colNumber - 1];
      if (header) {
        const value = cell.value;

        // Handle different cell value types
        if (value === null || value === undefined) {
          rowData[header] = undefined;
        } else if (typeof value === 'string') {
          rowData[header] = value.trim();
        } else if (typeof value === 'number' || typeof value === 'boolean') {
          rowData[header] = value;
        } else if (typeof value === 'object' && 'text' in value) {
          // Handle rich text
          rowData[header] = value.text?.toString().trim() || '';
        } else if (value instanceof Date) {
          rowData[header] = value.toISOString();
        } else {
          rowData[header] = value.toString().trim();
        }
      }
    });

    data.push(rowData);
  });

  return { data, headers };
};

const normalizeHeaderAlias = (header: string): string =>
  header
    .toLowerCase()
    .trim()
    .replace(/\s*\([^)]*\)\s*$/, '')
    .trim();

export const mapHeaders = (
  headers: string[],
  fieldMapping: Record<string, string[]>,
): Record<string, string> => {
  const headerMap: Record<string, string> = {};

  headers.forEach((header) => {
    const normalizedHeader = normalizeHeaderAlias(header);

    for (const [fieldName, possibleNames] of Object.entries(fieldMapping)) {
      if (
        possibleNames.some(
          (name) => normalizeHeaderAlias(name) === normalizedHeader,
        )
      ) {
        headerMap[header] = fieldName;
        break;
      }
    }
    if (!headerMap[header]) {
      headerMap[header] = header;
    }
  });

  return headerMap;
};
